import Button from '@/components/common/Button';
import Image from 'next/image';
import { useState } from 'react';
import ModalComponent from '../modal/ModalComponent';
import { MdClose } from 'react-icons/md';
import { TextWithDropShadow } from '../TextWithDropShadow';
import { twMerge } from 'tailwind-merge';

export const Seer2Card = () => {
  const [modalQuick, setModalQuick] = useState<boolean>(false);

  const data = [
    {
      title: 'What SEER2 measures',
      content:
        'SEER2 assesses the efficiency of air conditioners and heat pumps, indicating how much cooling output they provide over a typical cooling season compared to the energy they consume.',
    },
    {
      title: 'Why it matters',
      content:
        'The higher the SEER2 rating, the more efficient the unit, meaning it uses less energy to achieve the same cooling output',
    },
    {
      title: 'New standard',
      content:
        'SEER2 is the updated standard for measuring the cooling efficiency of air conditioners and heat pumps, replacing the older SEER rating. ',
    },
    {
      title: 'Revised Testing',
      content:
        "It uses a new M1 blower testing procedure that increases the system's external static pressure by a factor of five, better reflecting real-world conditions of installed equipment",
    },
    {
      title: 'Lower ratings',
      content:
        "Due to the revised testing method, you'll likely see lower SEER2 ratings compared to the old SEER ratings, even if the equipment performs the same.",
    },
    {
      title: 'Focus on Real-World Performance',
      content:
        'The new standard aims to provide a more accurate representation of how HVAC systems perform in real-world conditions, leading to more informed decisions for consumers.',
    },
    {
      title: 'Impact on Efficiency',
      content:
        'A higher SEER2 rating still indicates greater energy efficiency, leading to lower energy bills and potentially higher levels of comfort.',
    },
  ];

  const contentAlignment: Record<string, string> = {
    top: 'items-center justify-start pt-24',
    middle: 'items-center  justify-center',
    bottom: 'items-center justify-end pb-18',
  };

  return (
    <>
      <div
        className={twMerge(
          'bg-contain bg-center bg-no-repeat sm:bg-cover',
          'bg-[url(https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto,f_auto,c_scale,w_390/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/mrcool_5th_seer2_xs_phkrpv)]',
          'sm:bg-[url(https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto,f_auto,c_scale,w_640/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/mrcool_5th_seer2_sm_vyy4yt)]',
          'md:bg-[url(https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto,f_auto,c_scale,w_1024/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/mrcool_5th_seer2_md_q5sqrt)]',
          'lg:bg-[url(https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto,f_auto,c_scale,w_1280/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/mrcool_5th_seer2_lg_adnzaf)]',
          'xl:bg-[url(https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto,f_auto,c_scale,w_1920/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/mrcool_5th_seer2_xl_rmgel9)]',
          'h-[550px] w-full rounded-3xl p-9 sm:h-[900px]'
        )}
      >
        <div
          className={twMerge(
            'relative flex h-full w-full flex-col p-4 text-center sm:p-7',
            contentAlignment['bottom'] || null
          )}
        >
          <div className='md:px-10 lg:px-32 xl:px-64'>
            <TextWithDropShadow
              text={
                "[size](md|sm:text-xl|md:text-2xl)The MRCOOL® DIY® minimizes energy waste, maximizing savings without sacrificing comfort. It's not just efficient, it's exceptionally efficient.[size]"
              }
              color={{
                text: 'text-white',
                shadow: 'text-white',
              }}
              displayShadow={false}
            />
          </div>
          <Button
            className='pill-button mx-auto mt-6'
            title='Learn more'
            onClick={() => setModalQuick(true)}
          >
            Learn More About SEER2
          </Button>

          <Image
            className='absolute bottom-0 left-0 hidden sm:flex'
            src='https://res.cloudinary.com/dbkbadq4w/image/upload/q_auto/f_auto/web/mrcool.com/5th%20Gen%20DIY/Page%20Assets/energy-star_oehgcm'
            alt='SEER2'
            width={90}
            height={95}
          />
        </div>
      </div>

      <ModalComponent
        containerClass='mb-10h-[calc(100vh-14rem)]'
        size='xl'
        isOpen={modalQuick}
        close={() => setModalQuick(false)}
      >
        <div className='relative flex h-[100vh-14rem] max-h-[600px] flex-col items-end'>
          <div className='absolute right-0 top-0 pr-5 pt-5'>
            <button
              onClick={() => setModalQuick(false)}
              className='flex items-center rounded-lg p-1'
            >
              <MdClose className='size-4 text-inherit' />
            </button>
          </div>
          <div className='px-6 pb-5'>
            <div className='flex flex-col gap-y-5 px-10 py-6 text-center'>
              <TextWithDropShadow
                text={'[size](2xl|md:text-5xl)**What is SEER2?**[size]'}
                displayShadow={false}
              />
            </div>
            <div className='max-h-[400px] overflow-y-auto md:px-40'>
              {data.map((item, index) => {
                return (
                  <div className='pb-5' key={index}>
                    <div className='text-xl font-bold'>{item.title}</div>
                    <div className='text-base font-[500] text-gray-500'>
                      {item.content}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};
